import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Trophy, GitBranch, LayoutGrid, Plus, Trash2, Eye, Play, CheckCircle2, Settings } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TournamentPanel({ orgId, userRole }) {
  const { isDark } = useTheme();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeTournament, setActiveTournament] = useState(null);
  const [tournamentDetail, setTournamentDetail] = useState(null);
  const [standings, setStandings] = useState([]);
  const [detailView, setDetailView] = useState('games'); // games, standings

  const [form, setForm] = useState({ name: '', sport: '', start_date: '', end_date: '', location: '', format: 'bracket', teams_text: '', rules: '' });

  const canManage = ['company_support', 'ad', 'scheduler'].includes(userRole);

  useEffect(() => { if (orgId) fetchTournaments(); }, [orgId]);

  const fetchTournaments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tournaments/${orgId}`, { withCredentials: true });
      setTournaments(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createTournament = async () => {
    try {
      const teams = form.teams_text.split('\n').map(t => t.trim()).filter(Boolean);
      await axios.post(`${API_URL}/api/tournaments/${orgId}`, {
        ...form, teams, teams_text: undefined
      }, { withCredentials: true });
      setShowCreate(false);
      setForm({ name: '', sport: '', start_date: '', end_date: '', location: '', format: 'bracket', teams_text: '', rules: '' });
      fetchTournaments();
    } catch (err) { console.error(err); }
  };

  const viewTournament = async (tournamentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/tournaments/tournament/${tournamentId}`, { withCredentials: true });
      setTournamentDetail(res.data);
      setActiveTournament(tournamentId);
      setDetailView('games');
      fetchStandings(tournamentId);
    } catch (err) { console.error(err); }
  };

  const fetchStandings = async (tournamentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/tournaments/tournament/${tournamentId}/standings`, { withCredentials: true });
      setStandings(res.data || []);
    } catch (err) { console.error(err); }
  };

  const generateBracket = async (tournamentId) => {
    try {
      await axios.post(`${API_URL}/api/tournaments/tournament/${tournamentId}/generate-bracket`, {}, { withCredentials: true });
      viewTournament(tournamentId);
    } catch (err) { console.error(err); }
  };

  const updateScore = async (gameId, homeScore, awayScore, status) => {
    try {
      await axios.put(`${API_URL}/api/tournaments/game/${gameId}/score`, {
        home_score: parseInt(homeScore) || 0,
        away_score: parseInt(awayScore) || 0,
        status: status || 'final'
      }, { withCredentials: true });
      viewTournament(activeTournament);
    } catch (err) { console.error(err); }
  };

  const deleteTournament = async (id) => {
    if (!window.confirm('Delete this tournament?')) return;
    try {
      await axios.delete(`${API_URL}/api/tournaments/tournament/${id}`, { withCredentials: true });
      setActiveTournament(null);
      setTournamentDetail(null);
      fetchTournaments();
    } catch (err) { console.error(err); }
  };

  const statusBadge = (status) => {
    const styles = { setup: 'bg-yellow-500/10 text-yellow-600', active: 'bg-green-500/10 text-green-600', completed: 'bg-slate-500/10 text-slate-600' };
    return <Badge className={styles[status] || styles.setup}>{status}</Badge>;
  };

  // Tournament detail view
  if (activeTournament && tournamentDetail) {
    const rounds = {};
    (tournamentDetail.games || []).forEach(g => {
      const key = g.pool || `Round ${g.round}`;
      if (!rounds[key]) rounds[key] = [];
      rounds[key].push(g);
    });

    return (
      <div className="space-y-4" data-testid="tournament-detail">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => { setActiveTournament(null); setTournamentDetail(null); }}
            className={isDark ? 'border-slate-600 text-slate-300' : ''}>Back</Button>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{tournamentDetail.name}</h2>
          {statusBadge(tournamentDetail.status)}
          {tournamentDetail.format && <Badge variant="outline" className="text-xs">{tournamentDetail.format}</Badge>}
        </div>

        <div className="flex gap-2">
          <Button size="sm" variant={detailView === 'games' ? 'default' : 'outline'}
            onClick={() => setDetailView('games')}
            className={detailView === 'games' ? 'bg-amber-600 text-white' : isDark ? 'border-slate-600 text-slate-300' : ''}>
            Games ({tournamentDetail.games?.length || 0})
          </Button>
          <Button size="sm" variant={detailView === 'standings' ? 'default' : 'outline'}
            onClick={() => setDetailView('standings')}
            className={detailView === 'standings' ? 'bg-amber-600 text-white' : isDark ? 'border-slate-600 text-slate-300' : ''}>
            Standings
          </Button>
          {canManage && tournamentDetail.status === 'setup' && tournamentDetail.teams?.length >= 2 && (
            <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={() => generateBracket(activeTournament)}
              data-testid="generate-bracket-btn">
              <Play className="h-4 w-4 mr-1" /> Generate Bracket
            </Button>
          )}
        </div>

        {detailView === 'games' && (
          <div className="space-y-4">
            {Object.entries(rounds).map(([roundName, games]) => (
              <Card key={roundName} className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
                <CardHeader className="py-3">
                  <CardTitle className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{roundName}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {games.map(game => (
                    <GameRow key={game.id} game={game} isDark={isDark} canManage={canManage} onScore={updateScore} />
                  ))}
                </CardContent>
              </Card>
            ))}
            {(!tournamentDetail.games || tournamentDetail.games.length === 0) && (
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
                <CardContent className="py-8 text-center">
                  <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                    {tournamentDetail.status === 'setup' ? 'Generate bracket to create games' : 'No games yet'}
                  </p>
                  {tournamentDetail.teams?.length > 0 && (
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      Teams: {tournamentDetail.teams.join(', ')}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {detailView === 'standings' && (
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
            <CardContent className="p-0">
              {standings.length > 0 ? (
                <table className={`w-full text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                      <th className="text-left p-3">#</th>
                      <th className="text-left p-3">Team</th>
                      <th className="text-center p-3">W</th>
                      <th className="text-center p-3">L</th>
                      <th className="text-center p-3">T</th>
                      <th className="text-center p-3">PF</th>
                      <th className="text-center p-3">PA</th>
                      <th className="text-center p-3">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((s, i) => (
                      <tr key={s.team} className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                        <td className="p-3 font-medium">{i + 1}</td>
                        <td className="p-3 font-medium">{s.team}</td>
                        <td className="p-3 text-center text-green-500 font-bold">{s.w}</td>
                        <td className="p-3 text-center text-red-500">{s.l}</td>
                        <td className="p-3 text-center">{s.t}</td>
                        <td className="p-3 text-center">{s.pf}</td>
                        <td className="p-3 text-center">{s.pa}</td>
                        <td className={`p-3 text-center font-medium ${s.pf - s.pa > 0 ? 'text-green-500' : s.pf - s.pa < 0 ? 'text-red-500' : ''}`}>
                          {s.pf - s.pa > 0 ? '+' : ''}{s.pf - s.pa}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  <p>No completed games yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Tournament list view
  return (
    <div className="space-y-4" data-testid="tournament-panel">
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Trophy className="h-5 w-5 text-yellow-500" /> Tournaments
              <Badge variant="outline" className="text-xs">{tournaments.length}</Badge>
            </CardTitle>
            {canManage && (
              <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700"
                onClick={() => setShowCreate(true)} data-testid="tournament-create-btn">
                <Plus className="h-4 w-4 mr-1" /> New Tournament
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading...</p> :
          tournaments.length > 0 ? (
            <div className="space-y-2">
              {tournaments.map(t => (
                <div key={t.id} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${isDark ? 'border-slate-700 bg-slate-800/30 hover:bg-slate-700/50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'}`}
                  onClick={() => viewTournament(t.id)}>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.name}</p>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {t.sport} | {t.start_date} - {t.end_date} | {t.location}
                    </p>
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {t.teams?.length || 0} teams | {t.game_count || 0} games | {t.format}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(t.status)}
                    {canManage && (
                      <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); deleteTournament(t.id); }} className="text-red-400">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <Trophy className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No Tournaments</p>
              <p className="text-sm mt-1">Create tournament events with brackets and pool play</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CREATE TOURNAMENT DIALOG */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}>
          <DialogHeader><DialogTitle>New Tournament</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Name</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                placeholder="e.g., Spring Invitational" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="tournament-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Sport</Label>
                <Input value={form.sport} onChange={e => setForm({...form, sport: e.target.value})}
                  placeholder="e.g., Basketball" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
              </div>
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Location</Label>
                <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
              </div>
              <div>
                <Label className={isDark ? 'text-slate-300' : ''}>End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} />
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Format</Label>
              <div className="flex gap-2 mt-1">
                {[{f:'bracket', icon: GitBranch, l:'Single Elim'}, {f:'pool', icon: LayoutGrid, l:'Pool Play'}, {f:'round_robin', icon: LayoutGrid, l:'Round Robin'}].map(({f, icon: Icon, l}) => (
                  <Button key={f} size="sm" variant={form.format === f ? 'default' : 'outline'}
                    onClick={() => setForm({...form, format: f})}
                    className={form.format === f ? 'bg-amber-600 text-white' : isDark ? 'border-slate-600 text-slate-300' : ''}>
                    <Icon className="h-4 w-4 mr-1" /> {l}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label className={isDark ? 'text-slate-300' : ''}>Teams (one per line)</Label>
              <textarea value={form.teams_text} onChange={e => setForm({...form, teams_text: e.target.value})}
                rows={4} placeholder={"Team A\nTeam B\nTeam C\nTeam D"}
                className={`w-full rounded px-3 py-2 border text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`} />
            </div>
            <Button onClick={createTournament} className="w-full bg-amber-600 text-white hover:bg-amber-700" data-testid="create-tournament-btn">
              <Trophy className="h-4 w-4 mr-1" /> Create Tournament
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Individual game row with inline scoring
function GameRow({ game, isDark, canManage, onScore }) {
  const [editing, setEditing] = useState(false);
  const [homeScore, setHomeScore] = useState(game.home_score ?? '');
  const [awayScore, setAwayScore] = useState(game.away_score ?? '');

  return (
    <div className={`flex items-center justify-between p-2 rounded border ${isDark ? 'border-slate-700 bg-slate-800/20' : 'border-slate-100 bg-white'}`}>
      <div className="flex items-center gap-2 flex-1">
        <span className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>G{game.game_number}</span>
        <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{game.home_team}</span>
        <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>vs</span>
        <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{game.away_team}</span>
      </div>
      <div className="flex items-center gap-2">
        {game.status === 'final' && (
          <div className="flex items-center gap-1 text-sm">
            <span className={`font-bold ${(game.home_score ?? 0) > (game.away_score ?? 0) ? 'text-green-500' : isDark ? 'text-slate-400' : 'text-slate-600'}`}>{game.home_score}</span>
            <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>-</span>
            <span className={`font-bold ${(game.away_score ?? 0) > (game.home_score ?? 0) ? 'text-green-500' : isDark ? 'text-slate-400' : 'text-slate-600'}`}>{game.away_score}</span>
            <Badge className="bg-green-500/10 text-green-600 text-xs ml-1">Final</Badge>
          </div>
        )}
        {game.status === 'bye' && <Badge className="bg-slate-500/10 text-slate-500 text-xs">BYE</Badge>}
        {game.status === 'scheduled' && canManage && !editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}
            className={`text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}>Score</Button>
        )}
        {editing && (
          <div className="flex items-center gap-1">
            <Input type="number" value={homeScore} onChange={e => setHomeScore(e.target.value)}
              className={`w-12 h-7 text-xs text-center ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} />
            <span>-</span>
            <Input type="number" value={awayScore} onChange={e => setAwayScore(e.target.value)}
              className={`w-12 h-7 text-xs text-center ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`} />
            <Button size="sm" className="h-7 bg-green-600 text-white text-xs" onClick={() => { onScore(game.id, homeScore, awayScore, 'final'); setEditing(false); }}>
              <CheckCircle2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
